import {
  NODE_HEIGHT,
  NODE_WIDTH,
} from './assessment-attack-graph-layout.constants';

export const ATTACK_PATH_COMPACT_MIN_ZOOM = 0.45;

export type AttackPathViewportNode = {
  position: { x: number; y: number };
  measured?: { width?: number; height?: number };
  style?: { width?: number | string; height?: number | string };
};

export type AttackPathContentBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

const resolveDimension = ({
  styled,
  measured,
  fallback,
}: {
  styled: number | string | undefined;
  measured: number | undefined;
  fallback: number;
}): number => {
  if (typeof styled === 'number') {
    return styled;
  }

  return measured !== undefined && measured > 0 ? measured : fallback;
};

export const resolveAttackPathContentBounds = (
  nodes: ReadonlyArray<AttackPathViewportNode>,
): AttackPathContentBounds | null => {
  if (nodes.length === 0) {
    return null;
  }

  return nodes.reduce<AttackPathContentBounds>(
    (current, node) => {
      const width = resolveDimension({
        styled: node.style?.width,
        measured: node.measured?.width,
        fallback: NODE_WIDTH,
      });
      const height = resolveDimension({
        styled: node.style?.height,
        measured: node.measured?.height,
        fallback: NODE_HEIGHT,
      });

      return {
        minX: Math.min(current.minX, node.position.x),
        minY: Math.min(current.minY, node.position.y),
        maxX: Math.max(current.maxX, node.position.x + width),
        maxY: Math.max(current.maxY, node.position.y + height),
      };
    },
    {
      minX: Number.POSITIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
    },
  );
};

export const resolveAttackPathAnchoredViewport = ({
  bounds,
  containerWidth,
  containerHeight,
  minZoom,
  maxZoom = 1,
  padding = 12,
}: {
  bounds: AttackPathContentBounds;
  containerWidth: number;
  containerHeight: number;
  minZoom: number;
  maxZoom?: number;
  padding?: number;
}): { x: number; y: number; zoom: number } => {
  const contentWidth = Math.max(0, bounds.maxX - bounds.minX);
  const contentHeight = Math.max(0, bounds.maxY - bounds.minY);
  const availableWidth = Math.max(1, containerWidth - padding * 2);
  const availableHeight = Math.max(1, containerHeight - padding * 2);
  const fitZoom =
    contentWidth === 0 || contentHeight === 0
      ? maxZoom
      : Math.min(
          availableWidth / contentWidth,
          availableHeight / contentHeight,
        );
  const zoom = Math.min(maxZoom, Math.max(minZoom, fitZoom));
  const scaledWidth = contentWidth * zoom;
  const scaledHeight = contentHeight * zoom;

  return {
    zoom,
    x:
      scaledWidth <= availableWidth
        ? padding + (availableWidth - scaledWidth) / 2 - bounds.minX * zoom
        : padding - bounds.minX * zoom,
    y:
      scaledHeight <= availableHeight
        ? padding + (availableHeight - scaledHeight) / 2 - bounds.minY * zoom
        : padding - bounds.minY * zoom,
  };
};
